import React, { useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { createPost } from '../store/slices/postsSlice';
import { uploadAPI } from '../services/api';
import './CreatePost.css';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILES = 10;

interface MediaPreview {
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
}

interface CreatePostProps {
  latitude: number;
  longitude: number;
  onPostCreated?: () => void;
}

const CreatePost: React.FC<CreatePostProps> = ({ latitude, longitude, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState<MediaPreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_FILES - mediaFiles.length;
    if (remaining <= 0) {
      alert(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const newFiles: MediaPreview[] = [];
    for (const file of files.slice(0, remaining)) {
      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
      const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);

      if (!isImage && !isVideo) {
        alert(`Unsupported file type: ${file.name}. Allowed: JPEG, PNG, GIF, WebP, MP4, MOV, WebM`);
        continue;
      }

      if (isImage && file.size > MAX_IMAGE_SIZE) {
        alert(`Image ${file.name} exceeds 10MB limit`);
        continue;
      }

      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        alert(`Video ${file.name} exceeds 100MB limit`);
        continue;
      }

      newFiles.push({
        file,
        previewUrl: URL.createObjectURL(file),
        type: isImage ? 'image' : 'video',
      });
    }

    setMediaFiles((prev) => [...prev, ...newFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    setMediaFiles((prev) => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim() && mediaFiles.length === 0) return;

    let mediaItems: any[] | undefined;

    if (mediaFiles.length > 0) {
      setUploading(true);
      try {
        const uploadResults = await Promise.all(
          mediaFiles.map((m) => uploadAPI.uploadMedia(m.file))
        );
        mediaItems = uploadResults.map((res) => res.data);
      } catch (err) {
        alert('Failed to upload media. Please try again.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    await dispatch(createPost({
      content: content.trim(),
      media_items: mediaItems,
      latitude,
      longitude,
      location_name: 'Current Location',
    }));

    setContent('');
    mediaFiles.forEach((m) => URL.revokeObjectURL(m.previewUrl));
    setMediaFiles([]);
    setIsExpanded(false);
    onPostCreated?.();
  };

  return (
    <form className="create-post" onSubmit={handleSubmit}>
      <img
        src={user?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.username}`}
        alt={user?.display_name}
        className="avatar"
      />

      <div className="create-post-content">
        <textarea
          placeholder="What's happening in your area?"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onFocus={() => setIsExpanded(true)}
          maxLength={500}
          rows={isExpanded ? 3 : 1}
        />

        {isExpanded && (
          <>
            {mediaFiles.length > 0 && (
              <div className="media-preview-grid">
                {mediaFiles.map((media, index) => (
                  <div key={index} className="media-preview-item">
                    {media.type === 'image' ? (
                      <img src={media.previewUrl} alt="Preview" className="media-preview-img" />
                    ) : (
                      <video src={media.previewUrl} className="media-preview-video" muted />
                    )}
                    <button type="button" className="remove-media-btn" onClick={() => removeMedia(index)}>
                      &times;
                    </button>
                    {media.type === 'video' && (
                      <span className="media-type-badge">Video</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />

            <div className="create-post-footer">
              <div className="footer-left">
                <button
                  type="button"
                  className="add-image-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={mediaFiles.length >= MAX_FILES}
                >
                  Add Media ({mediaFiles.length}/{MAX_FILES})
                </button>
                <div className="location-info">
                  <span className="verified-location">Location set</span>
                </div>
              </div>

              <div className="post-actions">
                <span className="char-count">{content.length}/500</span>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={(!content.trim() && mediaFiles.length === 0) || uploading}
                >
                  {uploading ? 'Uploading...' : 'Post'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default CreatePost;
